import { Flex, Spinner } from "@radix-ui/themes";
import { ManifestLoadedEvent, MediaPlayer } from "dashjs";
import { useEffect, useState } from "react";
import { useRef } from "react";
import toast from "react-hot-toast";
import { canStartVideo, PlayerState, setSegments } from "@/entities/player";
import { useAppDispatch, useAppStore } from "@/app/appStore";
import { useVideoSourceGenerator } from "@/shared/hooks/useVideoSourceGenerator";
import {
  calculateSeekFromDVRWindowStart,
  isWithinRange,
} from "@/shared/helpers";
import CalculateSegmentGapsWorker from "@/shared/lib/calculate-segment-gaps.worker.ts?worker";
import { useMediaPlayer } from "@/shared/ui";
import { ManifestLoadedData } from "@/shared/types";

type VideoPlayerProps = {
  params: PlayerState["videoStream"];
  timeshift?: number;
  onTimeChange: (time: number | null, liveEdge: number | null) => void;
  onManifestLoaded: (data: ManifestLoadedEvent) => void;
};

export function VideoPlayer(props: VideoPlayerProps) {
  const shouldDeferCurrentTimeUpdate = useRef(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaPlayer = useMediaPlayer();
  const store = useAppStore();
  const dispatch = useAppDispatch();
  const prevIsPlaying = useRef(true);
  const prevIsLive = useRef(false);

  const generateVideoSource = useVideoSourceGenerator({
    timeshift: props.timeshift || 0,
    params: props.params,
  });

  useEffect(() => {
    if (!videoRef.current) return;

    const { liveDelayThreshold } = store.getState().player.preferences;

    mediaPlayer.initialize(videoRef.current);
    mediaPlayer.updateSettings({
      streaming: {
        delay: {
          liveDelay: liveDelayThreshold,
        },
        gaps: {
          enableSeekFix: true,
        },
      },
    });
    setIsInitialized(true);

    const onTimeChange = () => {
      if (shouldDeferCurrentTimeUpdate.current) {
        return;
      }

      const { availabilityStartTime } = store.getState().player.videoStream;

      const timeAsUTC = mediaPlayer.isDynamic()
        ? mediaPlayer.timeAsUTC() * 1000
        : availabilityStartTime
          ? availabilityStartTime + mediaPlayer.time() * 1000
          : 0;

      const liveEdge = mediaPlayer.isDynamic()
        ? mediaPlayer
            ?.getDashMetrics()
            ?.getCurrentDVRInfo()
            .manifestInfo.availableFrom.getTime() +
          mediaPlayer.getDashMetrics()?.getCurrentDVRInfo()?.range.end * 1000 -
          mediaPlayer.getTargetLiveDelay() * 1000
        : 0;

      props.onTimeChange(timeAsUTC, liveEdge);
    };

    const onManifestLoaded = (e: ManifestLoadedEvent) => {
      props.onManifestLoaded(e);

      const manifestData = e.data as ManifestLoadedData;
      const periods = manifestData?.Period;

      if (periods && Array.isArray(periods)) {
        const worker = new CalculateSegmentGapsWorker();
        worker.postMessage({
          periods: periods,
          availabilityStartTime: manifestData.availabilityStartTime,
        });
        worker.onmessage = (event: MessageEvent) => {
          dispatch(setSegments(event.data.gaps));
          worker.terminate();
        };
      }
    };

    const onError = (e: any) => {
      console.error(e);
      shouldDeferCurrentTimeUpdate.current = false;

      if (typeof e.error.data.response.data === "string") {
        toast.error(JSON.parse(e.error.data.response.data)?.detail);
      } else {
        toast.error(e.error.message);
      }

      setIsLoading(false);
    };

    const onWaiting = () => {
      setIsLoading(true);
    };

    const onPlaying = () => {
      prevIsPlaying.current = true;
      setIsLoading(false);
    };

    const onPaused = () => {
      prevIsPlaying.current = false;
    };

    mediaPlayer.on(MediaPlayer.events.PLAYBACK_TIME_UPDATED, onTimeChange);
    mediaPlayer.on(MediaPlayer.events.MANIFEST_LOADED, onManifestLoaded);
    mediaPlayer.on(MediaPlayer.events.ERROR, onError);
    mediaPlayer.on(MediaPlayer.events.PLAYBACK_WAITING, onWaiting);
    mediaPlayer.on(MediaPlayer.events.PLAYBACK_PLAYING, onPlaying);
    mediaPlayer.on(MediaPlayer.events.PLAYBACK_PAUSED, onPaused);

    return () => {
      mediaPlayer.off(MediaPlayer.events.ERROR, onError);
      mediaPlayer.off(MediaPlayer.events.PLAYBACK_TIME_UPDATED, onTimeChange);
      mediaPlayer.off(MediaPlayer.events.MANIFEST_LOADED, onManifestLoaded);
      mediaPlayer.off(MediaPlayer.events.PLAYBACK_WAITING, onWaiting);
      mediaPlayer.off(MediaPlayer.events.PLAYBACK_PLAYING, onPlaying);
      mediaPlayer.off(MediaPlayer.events.PLAYBACK_PAUSED, onPaused);
    };
  }, []);

  const handleAutoPlay = (isLive: boolean) => {
    if (prevIsLive.current !== isLive) {
      prevIsLive.current = isLive;
      mediaPlayer.setAutoPlay(true);
    } else {
      mediaPlayer.setAutoPlay(prevIsPlaying.current);
    }
  };

  useEffect(() => {
    if (!isInitialized) return;

    const state = store.getState();

    if (!canStartVideo(state)) {
      return;
    }

    const { currentTime } = state.player;
    const { isLive, fromTime, toTime, arbitraryFromTime, arbitraryToTime } =
      props.params;

    /**
     * Keep the new video playing from the current time if camera has changed.
     * Defer the current time update until the PLAYBACK_LOADED_DATA event to avoid cursor flickering.
     */
    if (
      currentTime &&
      (isLive ||
        isWithinRange(currentTime, fromTime, toTime) ||
        isWithinRange(currentTime, arbitraryFromTime, arbitraryToTime))
    ) {
      shouldDeferCurrentTimeUpdate.current = true;

      const onStreamInitialized = () => {
        shouldDeferCurrentTimeUpdate.current = false;
        mediaPlayer.off(
          MediaPlayer.events.STREAM_INITIALIZED,
          onStreamInitialized
        );

        mediaPlayer.setAutoPlay(true);
        mediaPlayer.seek(
          calculateSeekFromDVRWindowStart(currentTime, mediaPlayer)
        );
      };

      mediaPlayer.on(
        MediaPlayer.events.STREAM_INITIALIZED,
        onStreamInitialized
      );
    }

    setIsLoading(true);

    handleAutoPlay(props.params.isLive);
    mediaPlayer.attachSource(generateVideoSource());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mediaPlayer,
    props.params.fromTime,
    props.params.toTime,
    props.params.arbitraryFromTime,
    props.params.arbitraryToTime,
    props.params.cameraId,
    props.params.isLive,
    props.params.alignSegment,
    props.timeshift,
    isInitialized,
  ]);

  return (
    <div className="flex items-center justify-center w-full h-full bg-black">
      <video
        playsInline
        className="object-contain h-full aspect-video"
        ref={videoRef}
        muted
        autoPlay
        controls={import.meta.env.DEV}
      />

      {isLoading && (
        <Flex justify="center" align="center" className="absolute">
          <Spinner />
        </Flex>
      )}
    </div>
  );
}

export function VideoPlayerContainer(props: { children: React.ReactNode }) {
  return (
    <div className="w-full flex justify-center grow h-[calc(100dvh-350px)] max-h-[calc(100dvh-350px)]  md:h-[calc(100vh-276px)] md:max-h-[calc(100vh-276px)]">
      {props.children}
    </div>
  );
}
